export const TECHDEBT_ARTIFACT_CODE = `import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle, DollarSign, Users, Target, Activity } from 'lucide-react';

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  // Data
  const quarterlyData = [
    { quarter: 'Q4 2024', deals: 953, revenue: 47.9, avg: 16.0 },
    { quarter: 'Q1 2025', deals: 675, revenue: 37.2, avg: 12.4 },
    { quarter: 'Q2 2025', deals: 869, revenue: 48.8, avg: 16.3 },
    { quarter: 'Q3 2025', deals: 660, revenue: 29.0, avg: 9.7 }
  ];

  const pipelineData = [
    { name: 'Commit', value: 12.3, deals: 296, color: '#10b981' },
    { name: 'Best Case', value: 30.2, deals: 357, color: '#3b82f6' },
    { name: 'Pipeline', value: 128.3, deals: 1921, color: '#6366f1' },
    { name: 'Unqualified', value: 0.4, deals: 30, color: '#94a3b8' }
  ];

  const productData = [
    { name: 'DataForce Enterprise', revenue: 28.7, deals: 207, asp: 139 },
    { name: 'InsightMax Platform', revenue: 23.3, deals: 207, asp: 113 },
    { name: 'QuickAnalytics Pro', revenue: 17.6, deals: 314, asp: 56 },
    { name: 'ReportBuilder Suite', revenue: 13.4, deals: 624, asp: 21 },
    { name: 'TrendSpotter Analytics', revenue: 6.7, deals: 241, asp: 28 }
  ];

  const topReps = [
    { name: 'Marcus Chen', revenue: 27.7, deals: 113, asp: 245, trend: 'up' },
    { name: 'Jennifer Williams', revenue: 23.3, deals: 257, asp: 91, trend: 'up' },
    { name: 'David Thompson', revenue: 19.1, deals: 162, asp: 118, trend: 'stable' },
    { name: 'Sarah Martinez', revenue: 12.4, deals: 98, asp: 127, trend: 'up' },
    { name: 'Alex Kumar', revenue: 10.8, deals: 142, asp: 76, trend: 'down' }
  ];

  const stageData = [
    { stage: 'Need Analysis', deals: 378, value: 45.8, conversion: 42 },
    { stage: 'Opp Qualified', deals: 357, value: 33.5, conversion: 38 },
    { stage: 'Solution Proposal', deals: 411, value: 30.4, conversion: 35 },
    { stage: 'Vendor Finalist', deals: 207, value: 11.6, conversion: 52 },
    { stage: 'Verbal Approval', deals: 129, value: 11.8, conversion: 68 },
    { stage: 'In Legal', deals: 332, value: 12.2, conversion: 78 }
  ];

  const MetricCard = ({ title, value, subtitle, trend, icon: Icon, alert }) => (
    <div className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={\`p-2 rounded-lg \\\${alert ? 'bg-amber-50' : 'bg-gray-50'}\`}>
            <Icon className={\`w-5 h-5 \\\${alert ? 'text-amber-600' : 'text-gray-600'}\`} />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium">{title}</div>
            <div className="text-xl font-semibold text-gray-900 mt-0.5">{value}</div>
            {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {trend && (
          <div className={\`flex items-center gap-1 text-sm font-medium \\\${
            trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600'
          }\`}>
            {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : 
             trend === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
          </div>
        )}
      </div>
    </div>
  );

  const TabButton = ({ id, label, active }) => (
    <button
      onClick={() => setSelectedTab(id)}
      className={\`px-4 py-2 text-sm font-medium rounded-lg transition-colors \\\${
        active 
          ? 'bg-gray-900 text-white' 
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
      }\`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Strategic Revenue Assessment</h1>
              <p className="text-sm text-gray-500 mt-1">TechVenture Solutions • Last 12 Months</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="text-gray-500">Updated October 14, 2025</div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex gap-2">
            <TabButton id="overview" label="Overview" active={selectedTab === 'overview'} />
            <TabButton id="pipeline" label="Pipeline" active={selectedTab === 'pipeline'} />
            <TabButton id="products" label="Products" active={selectedTab === 'products'} />
            <TabButton id="team" label="Team" active={selectedTab === 'team'} />
            <TabButton id="insights" label="Insights" active={selectedTab === 'insights'} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MetricCard
                title="Closed Won Revenue"
                value="\\$163.4M"
                icon={DollarSign}
                trend="up"
              />
              <MetricCard
                title="Win Rate"
                value="58.7%"
                subtitle="3.7pts above industry"
                icon={Target}
                trend="up"
              />
              <MetricCard
                title="Active Pipeline"
                value="\\$171M"
                subtitle="2,604 opportunities"
                icon={Activity}
              />
              <MetricCard
                title="New Logos"
                value="118"
                subtitle="4% of total revenue"
                icon={Users}
                trend="down"
                alert={true}
              />
            </div>

            {/* Alert Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-amber-900">Growth Constraint Identified</div>
                  <div className="text-sm text-amber-700 mt-1">
                    New logo acquisition at only 4% of revenue. Target: 15-20% for healthy growth. 
                    332 deals stuck in Legal stage (\\$12.2M at risk).
                  </div>
                </div>
              </div>
            </div>

            {/* Quarterly Trend */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Quarterly Performance</h3>
                  <p className="text-sm text-gray-500 mt-1">Revenue and deal volume by quarter</p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setSelectedMetric('revenue')}
                    className={\`px-3 py-1.5 text-xs font-medium rounded-md transition-colors \\\${
                      selectedMetric === 'revenue' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                    }\`}
                  >
                    Revenue
                  </button>
                  <button 
                    onClick={() => setSelectedMetric('deals')}
                    className={\`px-3 py-1.5 text-xs font-medium rounded-md transition-colors \\\${
                      selectedMetric === 'deals' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                    }\`}
                  >
                    Deals
                  </button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={quarterlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="quarter" stroke="#64748b" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                  <Bar 
                    dataKey={selectedMetric === 'revenue' ? 'revenue' : 'deals'} 
                    fill="#6366f1" 
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-900">
                  <span className="font-medium">Seasonality Pattern:</span> Q4 & Q2 peak at \\$48M+, Q3 drops 40% to \\$29M
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'pipeline' && (
          <div className="space-y-6">
            {/* Pipeline Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-6">Pipeline Distribution</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pipelineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pipelineData.map((entry, index) => (
                        <Cell key={\`cell-\\\${index}\`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => \`\\$\\\${value}M\`}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {pipelineData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-700">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500">{item.deals} deals</span>
                        <span className="font-medium text-gray-900">\$\${item.value}M</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-6">Stage Analysis</h3>
                <div className="space-y-4">
                  {stageData.map((stage) => (
                    <div key={stage.stage} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-900">{stage.stage}</span>
                        <span className="text-gray-500">{stage.deals} deals • \$\${stage.value}M</span>
                      </div>
                      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="absolute h-full bg-indigo-500 rounded-full transition-all"
                          style={{ width: \`\\\${stage.conversion}%\` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500">
                        {stage.conversion}% conversion rate
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Critical Issues */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <div className="font-medium text-red-900">Legal Bottleneck</div>
                    <div className="text-sm text-red-700 mt-1">332 deals stuck</div>
                    <div className="text-lg font-semibold text-red-900 mt-2">\\$12.2M at risk</div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <div className="font-medium text-amber-900">Universe Stage</div>
                    <div className="text-sm text-amber-700 mt-1">Unqualified deals</div>
                    <div className="text-lg font-semibold text-amber-900 mt-2">787 deals</div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="font-medium text-blue-900">High Intent</div>
                    <div className="text-sm text-blue-700 mt-1">Verbal Approval</div>
                    <div className="text-lg font-semibold text-blue-900 mt-2">\\$11.8M</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'products' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-6">Product Performance</h3>
              <div className="space-y-4">
                {productData.map((product, index) => (
                  <div key={product.name} className="space-y-3 pb-4 border-b border-gray-100 last:border-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-semibold text-sm">
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-900">{product.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">\$\${product.revenue}M</div>
                        <div className="text-xs text-gray-500">{product.deals} deals</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm ml-11">
                      <div>
                        <span className="text-gray-500">Avg Deal Size: </span>
                        <span className="font-medium text-gray-900">\$\${product.asp}K</span>
                      </div>
                      <div className="relative flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="absolute h-full bg-indigo-500 rounded-full"
                          style={{ width: \`\\\${(product.revenue / 28.7) * 100}%\` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-3">Portfolio Strategy Gap</h4>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full mt-2" />
                  <span>DataForce & InsightMax drive 32% of revenue but only 15% of deals → Need to scale enterprise motion</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full mt-2" />
                  <span>ReportBuilder drives 23% of deals but only 8% of revenue → Profitability question</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full mt-2" />
                  <span>Services spread across 343 deals → Services burden on sales capacity</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'team' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-6">Top Performers</h3>
              <div className="space-y-4">
                {topReps.map((rep, index) => (
                  <div key={rep.name} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {rep.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-900">{rep.name}</div>
                        {index < 3 && (
                          <div className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                            Top {index + 1}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {rep.deals} deals • \$\${rep.asp}K avg deal size
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">\$\${rep.revenue}M</div>
                        <div className="text-xs text-gray-500">L12M Revenue</div>
                      </div>
                      <div>
                        {rep.trend === 'up' && <TrendingUp className="w-5 h-5 text-green-600" />}
                        {rep.trend === 'down' && <TrendingDown className="w-5 h-5 text-red-600" />}
                        {rep.trend === 'stable' && <div className="w-5 h-5" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900 mb-2">Concentration Risk</div>
                  <div className="text-sm text-amber-700 space-y-1">
                    <div>• Top 3 reps = 43% of total revenue</div>
                    <div>• Marcus Chen: Enterprise specialist at \\$245K ASP → needs to be cloned</div>
                    <div>• Jennifer Williams: Balanced producer with high volume</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'insights' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <h3 className="text-base font-semibold text-gray-900">Critical Issues</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="font-medium text-red-900 mb-1">New Logo Acquisition</div>
                    <div className="text-sm text-red-700">Only 4% of revenue from new customers vs 15-20% target</div>
                    <div className="text-xs text-red-600 mt-2">Impact: Growth capped at customer expansion rates</div>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="font-medium text-red-900 mb-1">Marketing Attribution Blackout</div>
                    <div className="text-sm text-red-700">99.4% of \\$162M revenue has missing lead source</div>
                    <div className="text-xs text-red-600 mt-2">Impact: \\$17-22M wasted marketing spend</div>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="font-medium text-amber-900 mb-1">Pipeline Discipline</div>
                    <div className="text-sm text-amber-700">787 unqualified deals in "Universe" stage</div>
                    <div className="text-xs text-amber-600 mt-2">Impact: Sales capacity drain, forecast inaccuracy</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <h3 className="text-base font-semibold text-gray-900">Quick Wins</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-green-900">Fix Lead Source</div>
                      <div className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">0-30 days</div>
                    </div>
                    <div className="text-sm text-green-700">Make lead source required, backfill historical data</div>
                    <div className="text-xs text-green-600 mt-2">ROI: Optimize \\$17-22M marketing spend</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-blue-900">Clear Legal Bottleneck</div>
                      <div className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">30-60 days</div>
                    </div>
                    <div className="text-sm text-blue-700">Implement tiered review, set SLAs</div>
                    <div className="text-xs text-blue-600 mt-2">ROI: Unlock \\$6M stuck pipeline</div>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-indigo-900">Pipeline Cleanup</div>
                      <div className="text-xs bg-indigo-200 text-indigo-800 px-2 py-1 rounded">60-90 days</div>
                    </div>
                    <div className="text-sm text-indigo-700">Implement MEDDIC, reduce stages to 7</div>
                    <div className="text-xs text-indigo-600 mt-2">ROI: 25% conversion improvement</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Growth Scenarios</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-sm font-medium text-gray-500 mb-2">Status Quo</div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">\\$169M</div>
                  <div className="text-sm text-red-600 font-medium mb-4">+4% Growth → Declining</div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>• 122 new logos</div>
                    <div>• -\\$2.4M churn</div>
                    <div>• 96.1% NRR</div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-300">
                  <div className="text-sm font-medium text-green-700 mb-2">Aggressive Growth</div>
                  <div className="text-3xl font-bold text-green-900 mb-1">\\$202M</div>
                  <div className="text-sm text-green-600 font-medium mb-4">+24% Growth → Scale Mode</div>
                  <div className="space-y-2 text-sm text-green-700">
                    <div>• 350 new logos (3x)</div>
                    <div>• -\\$2.0M churn (improved)</div>
                    <div>• 106% NRR</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                <div className="text-sm font-medium text-gray-900 mb-2">Investment Required: \\$1.8-2.4M</div>
                <div className="text-sm text-gray-600">
                  Expected Return: <span className="font-semibold text-green-600">\\$39M incremental ARR</span> at 75% margin = 
                  <span className="font-semibold text-green-600"> \\$29M gross profit</span> (12-16x ROI)
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return <Dashboard />;
}

export default App;
`;
